import { useCachedData } from '../hooks/useCachedData';
import { endpoints, joinUrl, deleteApiCall, putApiCall, api } from '../api/config';
import { extractUserList } from '../utils/userUtils';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useMemo, useState, useCallback, useEffect, forwardRef } from 'react';
import { Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, InputAdornment, CircularProgress, Snackbar, FormControl, InputLabel, Select, MenuItem, Stack, Tooltip, Checkbox, FormControlLabel, Switch, Chip, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
const normalizeRolesValue = (value) => {
  const roles = typeof value === 'string' ? value.split(',') : value;
  return Array.isArray(roles) ? roles.filter(Boolean) : [];
};

const RoleMenuPaper = forwardRef(function RoleMenuPaper({ onDone, menuId, children, ...props }, ref) {
  return (
    <Paper ref={ref} data-role-select-menu={menuId} {...props}>
      {children}
      <Box
        sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onDone?.();
          }}
        >
          Done
        </Button>
      </Box>
    </Paper>
  );
});

const INITIAL_CREATE_FORM_STATE = {
  name: '',
  password: '',
  location: '',
  roles: [],
  multi_location_enable: false,
  hide_on_mobile: false,
  features: {
    itemLookup: false,
    binCount: false,
    binAdjustments: false,
    binTransfer: false,
    inbound: false,
    ftzInbound: false,
    inboundReturnStatus: false,
    dropShip: false,
    amzlPicking: false,
    amxlPicking: false,
    shipConfirm: false,
    dropshipStatus: false,
    ltlDashboard: false,
    ltlOrders: false,
    reassignLtlOrders: false,
    dropshipReassign: false,
    reassignAmzlOrders: false,
    rma: false,
    nonAmazonRegularShip: false,
    partsPicking: false,
    binAdjustmentsReport: false,
    binCountReports: false,
    binTransferReports: false,
    managerOtp: false,
    inboundReports: false,
    pickingReports: false,
    ltlReports: false,
    regularReprint: false,
    ftzBinTransfer: false,
    ftzBinPutaway: false,
    ftzShipping: false,
    ftzShippingOrder: false,
    ftzReceivingOrder: false,
    ftzQrGenerate: false,
    inventoryManagerApproval: false,
    returnItem: false,
  },
};

const WmsAiUsers = () => {
  const WMS_COL_EDIT = '__wms_edit';
  const WMS_COL_DELETE = '__wms_delete';
  const { data, isLoading, error, refetch } = useCachedData(endpoints.wmsAiUsers, {
    refetchInterval: 30000, // 30 seconds
    staleTime: 20000, // 20 seconds
  });

  const { data: rolesData, isLoading: rolesLoading, refetch: refetchRoles } =
    useCachedData(endpoints.roles);

  // Fetch locations data
  const { data: locationsData, isLoading: locationsLoading } = useCachedData(endpoints.locations);

  // Transform locations data for dropdown
  const locationOptions = useMemo(() => {
    if (!locationsData) return [];

    // Handle both array and object formats
    if (Array.isArray(locationsData)) {
      return locationsData.map(loc => ({
        value: loc.location || loc.name || loc.id,
        label: loc.location || loc.name || loc.id
      }));
    }

    return Object.values(locationsData).map(loc => ({
      value: loc.location || loc.name || loc.id,
      label: loc.location || loc.name || loc.id
    }));
  }, [locationsData]);

  const roleOptions = useMemo(() => {
    if (!rolesData) return [];

    // New API shape: { web: [...], mobile: [...] }
    if (Array.isArray(rolesData.mobile)) {
      return rolesData.mobile.map((r) => r.role).filter(Boolean);
    }

    // Legacy shape: { roles: [...] } or plain array — filter to mobile only
    let roles = [];
    if (Array.isArray(rolesData.roles)) {
      roles = rolesData.roles;
    } else if (Array.isArray(rolesData)) {
      roles = rolesData;
    }
    return roles
      .filter((r) => r.platform === 'mobile')
      .map((r) => r.role)
      .filter(Boolean);
  }, [rolesData]);

  const assignRolesToUser = useCallback(async (userid, roles) => {
    if (!userid || !roles?.length) return;
    await api.post(endpoints.assignRoleToUser, { userid, roles, platform: 'mobile' });
  }, []);

  const userRolesByUserid = useMemo(() => {
    const map = {};
    if (!rolesData) return map;

    let roles = [];
    if (Array.isArray(rolesData.roles)) roles = rolesData.roles;
    else if (Array.isArray(rolesData)) roles = rolesData;

    roles.forEach((entry) => {
      const roleName = entry?.role ?? entry?.role_name;
      const userids = entry?.userids ?? [];
      if (!roleName) return;
      userids.forEach((uid) => {
        if (uid == null || uid === '') return;
        const key = String(uid);
        if (!map[key]) map[key] = [];
        if (!map[key].includes(roleName)) map[key].push(roleName);
      });
    });

    return map;
  }, [rolesData]);

  const resolveUserRoles = useCallback(
    (userid) => userRolesByUserid[String(userid)] || [],
    [userRolesByUserid]
  );

  const rolesSelectionChanged = useCallback((currentRoles = [], initialRoles = []) => {
    const current = [...currentRoles].sort().join(',');
    const initial = [...initialRoles].sort().join(',');
    return current !== initial;
  }, []);

  const renderRoleSelectValue = useCallback((selected, loading = false) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          Loading roles…
        </Box>
      );
    }
    const safeSelected = Array.isArray(selected) ? selected : [];
    if (!safeSelected.length) return <em>No roles</em>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {safeSelected.map((roleName) => (
          <Chip key={roleName} label={roleName} size="small" color="primary" variant="outlined" />
        ))}
      </Box>
    );
  }, []);

  const roleSelectMenuProps = useCallback((onDone, menuId) => ({
    autoFocus: false,
    disableAutoFocusItem: true,
    sx: { zIndex: 1400 },
    PaperProps: {
      component: RoleMenuPaper,
      onDone,
      menuId,
      sx: { maxHeight: 360 },
    },
    MenuListProps: {
      sx: { maxHeight: 300, overflow: 'auto', py: 0 },
    },
  }), []);

  const handleRolesMenuClose = useCallback((setMenuOpen) => (_, reason) => {
    if (reason === 'selectOption') return;
    setMenuOpen(false);
  }, []);

    const renderRoleMenuItems = useCallback((selectedRoles) => {
    const safeRoles = Array.isArray(selectedRoles) ? selectedRoles : [];
    return roleOptions.map((roleName) => (
      <MenuItem key={roleName} value={roleName} sx={{ gap: 1 }}>
        <Checkbox
          checked={safeRoles.includes(roleName)}
          size="small"
          disableRipple
          tabIndex={-1}
          sx={{ p: 0.5, pointerEvents: 'none' }}
        />
        {roleName}
      </MenuItem>
    ));
  }, [roleOptions]);

  const handleCreateRolesChange = useCallback((event) => {
    setForm((prev) => ({ ...prev, roles: normalizeRolesValue(event.target.value) }));
  }, []);

  const handleEditRolesChange = useCallback((event) => {
    setEditForm((prev) => ({ ...prev, roles: normalizeRolesValue(event.target.value) }));
  }, []);

  const [open, setOpen] = useState(false);
  const [createRolesMenuOpen, setCreateRolesMenuOpen] = useState(false);
  const [editRolesMenuOpen, setEditRolesMenuOpen] = useState(false);

  useEffect(() => {
    if (!createRolesMenuOpen && !editRolesMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (createRolesMenuOpen) {
        const insideCreate =
          target.closest('#roles') || target.closest('[data-role-select-menu="create"]');
        if (!insideCreate) setCreateRolesMenuOpen(false);
      }
      if (editRolesMenuOpen) {
        const insideEdit =
          target.closest('#edit-roles') || target.closest('[data-role-select-menu="edit"]');
        if (!insideEdit) setEditRolesMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [createRolesMenuOpen, editRolesMenuOpen]);

  const [form, setForm] = useState(INITIAL_CREATE_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingUserid, setDeletingUserid] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Password update states
  const [updatePasswordDialogOpen, setUpdatePasswordDialogOpen] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [updatePasswordForm, setUpdatePasswordForm] = useState({ userid: '', new_password: '' });
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState(false);
  const [updatePasswordError, setUpdatePasswordError] = useState('');
  const [updatePasswordSuccess, setUpdatePasswordSuccess] = useState('');
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Track which users' plaintext passwords are visible in the table
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const togglePasswordVisibility = useCallback((userid) => {
    setVisiblePasswords(prev => ({ ...prev, [userid]: !prev[userid] }));
  }, []);

  // Add column visibility state
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('wms-ai-users-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('wms-ai-users-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Add edit states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    userid: '',
    name: '',
    location: '',
    roles: [],
    password: '',
    confirm_password: '',
    multi_location_enable: false,
    hide_on_mobile: false,
    features: {
      itemLookup: false,
      binCount: false,
      binAdjustments: false,
      binTransfer: false,
      inbound: false,
      ftzInbound: false,
      inboundReturnStatus: false,
      dropShip: false,
      amzlPicking: false,
      amxlPicking: false,
      shipConfirm: false,
      dropshipStatus: false,
      ltlDashboard: false,
      ltlOrders: false,
      reassignLtlOrders: false,
      dropshipReassign: false,
      binAdjustmentsReport: false,
      binCountReports: false,
      binTransferReports: false,
      managerOtp: false,
      inboundReports: false,
      pickingReports: false,
      ltlReports: false,
      regularReprint: false,
      ftzBinTransfer: false,
      ftzBinPutaway: false,
      ftzShipping: false,
      ftzShippingOrder: false,
      ftzReceivingOrder: false,
      ftzQrGenerate: false,
      inventoryManagerApproval: false,
      reassignAmzlOrders: false,
      returnItem: false
    }
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  // Debug editForm when dialog opens
  useEffect(() => {
    if (editDialogOpen) {
      console.log('📋 Edit Form Features:', editForm.features);
    }
  }, [editDialogOpen, editForm]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editInitialRoles, setEditInitialRoles] = useState([]);
  const [editRoleLoading, setEditRoleLoading] = useState(false);

  // Add refresh loading state
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    setDeletingUserid(userToDelete.userid);
    setDeleteDialogOpen(false);

    try {
      await deleteApiCall(`/wms-ai-users/delete/${userToDelete.userid}`);

      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });

      // Refresh the data
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete user',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      setDeletingUserid(null);
      setUserToDelete(null);
    }
  };

  // Password update handlers
  const handleUpdatePasswordClick = (user) => {
    setUserToUpdate(user);
    setUpdatePasswordForm({ userid: user.userid, new_password: '' });
    setUpdatePasswordError('');
    setUpdatePasswordSuccess('');
    setShowUpdatePassword(false);
    setUpdatePasswordDialogOpen(true);
  };

  const handleUpdatePasswordCancel = () => {
    setUpdatePasswordDialogOpen(false);
    setUserToUpdate(null);
    setUpdatePasswordForm({ userid: '', new_password: '' });
    setUpdatePasswordError('');
    setUpdatePasswordSuccess('');
  };

  const handleUpdatePasswordChange = (e) => {
    const { name, value } = e.target;
    // Remove spaces from password
    const trimmedValue = value.replace(/\s+/g, '');
    setUpdatePasswordForm(prev => ({ ...prev, [name]: trimmedValue }));
  };

  const handleUpdatePasswordSubmit = async (e) => {
    e.preventDefault();
    setUpdatePasswordLoading(true);
    setUpdatePasswordError('');
    setUpdatePasswordSuccess('');

    // Validate password length
    if (updatePasswordForm.new_password.length < 4) {
      setUpdatePasswordError('Password must be at least 4 characters long');
      setUpdatePasswordLoading(false);
      return;
    }

    // Validate maximum password length
    if (updatePasswordForm.new_password.length > 15) {
      setUpdatePasswordError('Password cannot exceed 15 characters');
      setUpdatePasswordLoading(false);
      return;
    }

    // Validate no spaces in password
    if (updatePasswordForm.new_password.includes(' ')) {
      setUpdatePasswordError('Password cannot contain spaces');
      setUpdatePasswordLoading(false);
      return;
    }

    try {
      await putApiCall(`/wms-ai-users/update/${updatePasswordForm.userid}`, {
        new_password: updatePasswordForm.new_password
      });

      setUpdatePasswordSuccess('Password updated successfully!');
      setUpdatePasswordForm({ userid: '', new_password: '' });

      setTimeout(() => {
        setUpdatePasswordDialogOpen(false);
        setUpdatePasswordSuccess('');
        refetch();
      }, 1000);
    } catch (error) {
      setUpdatePasswordError(error.message || 'Failed to update password');
    } finally {
      setUpdatePasswordLoading(false);
    }
  };

  useEffect(() => {
    if (!editDialogOpen || !editForm.userid || !editRoleLoading || rolesLoading) return;

    let assignedRoles = [];
    if (data) {
      const user = Array.isArray(data)
        ? data.find(u => u.userid === editForm.userid)
        : (data[editForm.userid] || Object.values(data).find(u => u.userid === editForm.userid));
      if (user && Array.isArray(user.allocated_roles)) {
        assignedRoles = user.allocated_roles;
      }
    }
    if (!assignedRoles || assignedRoles.length === 0) {
      assignedRoles = resolveUserRoles(editForm.userid);
    }

    setEditInitialRoles(assignedRoles);
    setEditForm((prev) => ({ ...prev, roles: assignedRoles }));
    setEditRoleLoading(false);
  }, [editDialogOpen, editForm.userid, editRoleLoading, rolesLoading, resolveUserRoles, data]);

  const handleEditClick = (user) => {
    setEditRoleLoading(rolesLoading);

    // Get features from either 'features' or 'feature_permissions' field
    const userFeatures = user.features || user.feature_permissions || {
      itemLookup: false,
      binCount: false,
      binAdjustments: false,
      binTransfer: false,
      inbound: false,
      ftzInbound: false,
      inboundReturnStatus: false,
      dropShip: false,
      shipConfirm: false,
      dropshipStatus: false,
      ltlDashboard: false,
      ltlOrders: false,
      reassignLtlOrders: false,
      dropshipReassign: false,
      rma: false,
      nonAmazonRegularShip: false,
      partsPicking: false,
      binAdjustmentsReport: false,
      binCountReports: false,
      binTransferReports: false,
      managerOtp: false,
      inboundReports: false,
      pickingReports: false,
      ltlReports: false,
      regularReprint: false,
      ftzBinTransfer: false,
      ftzBinPutaway: false,
      ftzShipping: false,
      ftzShippingOrder: false,
      ftzReceivingOrder: false,
      ftzQrGenerate: false,
      inventoryManagerApproval: false,
      amzlPicking: false,
      returnItem: false
    };

    const assignedRoles = Array.isArray(user.allocated_roles)
      ? user.allocated_roles
      : resolveUserRoles(user.userid);

    setEditInitialRoles(assignedRoles);
    setEditForm({
      userid: user.userid,
      name: user.name || '',
      location: user.location || '',
      roles: assignedRoles,
      password: '',
      confirm_password: '',
      multi_location_enable: user.multi_location_enable || false,
      hide_on_mobile: user.hide_on_mobile || false,
      features: userFeatures
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditRolesMenuOpen(false);
    setEditForm({
      userid: '',
      name: '',
      location: '',
      roles: [],
      password: '',
      confirm_password: '',
      multi_location_enable: false,
      hide_on_mobile: false,
      features: {
        itemLookup: false,
        binCount: false,
        binAdjustments: false,
        binTransfer: false,
        inbound: false,
        dropShip: false,
        shipConfirm: false,
        dropshipStatus: false,
        ltlOrders: false,
        reassignLtlOrders: false,
        dropshipReassign: false,
        rma: false,
        nonAmazonRegularShip: false,
        partsPicking: false,
        binAdjustmentsReport: false,
        binCountReports: false,
        binTransferReports: false,
        managerOtp: false,
        inboundReports: false,
        pickingReports: false,
        ltlReports: false
      }
    });
    setEditError('');
    setEditInitialRoles([]);
    setEditRoleLoading(false);
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'multi_location_enable' || name === 'hide_on_mobile') {
      setEditForm(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name.startsWith('feature_')) {
      // Handle feature checkboxes
      const featureName = name.replace('feature_', '');
      setEditForm(prev => ({
        ...prev,
        features: {
          ...(prev.features || {}),
          [featureName]: checked
        }
      }));
    } else {
      // Remove spaces from password fields
      const trimmedValue = name.includes('password') ? value.replace(/\s+/g, '') : value;
      setEditForm(prev => ({
        ...prev,
        [name]: trimmedValue
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      // Validate password if it's being updated
      if (editForm.password || editForm.confirm_password) {
        if (editForm.password !== editForm.confirm_password) {
          setEditError('Passwords do not match');
          setEditLoading(false);
          return;
        }

        // Validate password length
        if (editForm.password.length < 4) {
          setEditError('Password must be at least 4 characters long');
          setEditLoading(false);
          return;
        }

        // Validate maximum password length
        if (editForm.password.length > 15) {
          setEditError('Password cannot exceed 15 characters');
          setEditLoading(false);
          return;
        }

        // Validate no spaces in password
        if (editForm.password.includes(' ')) {
          setEditError('Password cannot contain spaces');
          setEditLoading(false);
          return;
        }
      }

      // Make the PUT request to update user
      await api.put(`/wms-ai-users/update/${editForm.userid}`, {
        name: editForm.name,
        location: editForm.location,
        ...(editForm.password && { password: editForm.password }),
        multi_location_enable: editForm.multi_location_enable,
        hide_on_mobile: editForm.hide_on_mobile,
        features: editForm.features
      });

      const rolesChanged = rolesSelectionChanged(editForm.roles, editInitialRoles);
      if (rolesChanged && editForm.roles?.length) {
        await assignRolesToUser(editForm.userid, editForm.roles);
        await refetchRoles();
      }

      setSnackbar({
        open: true,
        message: rolesChanged && editForm.roles?.length
          ? 'User updated and roles assigned successfully'
          : 'User updated successfully',
        severity: 'success'
      });

      // Close the dialog and refresh the data
      handleEditClose();
      refetch();
    } catch (error) {
      console.error('Error updating user:', error.response?.data);
      setEditError(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // Transform the data for the table
  const userList = useMemo(() => extractUserList(data), [data]);

  const tableData = useMemo(() => {
    return userList
      .filter(row => row && typeof row === 'object' && row.userid !== 'jyswms_integration_user' && row.user_id !== 'jyswms_integration_user')
      .map((row, index) => {
        const uid = row.userid ?? row.user_id ?? row.id ?? `user_${index}`;
        return {
          ...row,
          id: uid,
          userid: row.userid ?? row.user_id ?? uid,
          [WMS_COL_EDIT]: true,
          [WMS_COL_DELETE]: true
        };
      });
  }, [userList]);

  // Generate columns with fixed order
  const columns = useMemo(() => {
    if (!userList || userList.length === 0) return [];

    const allKeysSet = new Set();
    userList.slice(0, 15).forEach(u => {
      if (u && typeof u === 'object') {
        Object.keys(u).forEach(k => {
          if (k !== 'actions' && k !== 'id' && k !== '_id') allKeysSet.add(k);
        });
      }
    });

    const baseColumns = [];

    // Helper function to create a column
    const createColumn = (key) => ({
      accessorKey: key,
      header: key === 'userid' || key === 'user_id'
        ? 'User ID'
        : key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      minSize: 180,
      size: 220,
      enableGlobalFilter: true,
      cell: info => {
        const value = info.getValue();
        const rowUser = info.row?.original;
        // Special handling for plaintext password column
        if (key === 'password_plaintext' || key === 'passwordPlaintext') {
          const plaintext = value || '';
          const uid = rowUser?.userid ?? rowUser?.user_id;
          const isVisible = uid && visiblePasswords[uid];
          const display = plaintext ? (isVisible ? String(plaintext) : '•'.repeat(String(plaintext).length || 8)) : '-';
          return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                {display}
              </Typography>
              <IconButton
                size="small"
                aria-label={isVisible ? 'Hide password' : 'Show password'}
                onClick={(e) => {
                  e.stopPropagation();
                  uid && togglePasswordVisibility(uid);
                }}
                disabled={!plaintext}
              >
                {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </Stack>
          );
        }
        // Handle arrays (such as allocated_roles: ["Picker"])
        if (Array.isArray(value)) {
          return value.length > 0 ? value.join(', ') : '-';
        }
        // Handle boolean values
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        // Handle objects
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        // Handle null/undefined values
        return value ?? '-';
      }
    });

    // 1. Name column (if exists)
    if (allKeysSet.has('name')) {
      baseColumns.push(createColumn('name'));
    }

    // 2. User ID column (if exists)
    if (allKeysSet.has('userid')) {
      baseColumns.push(createColumn('userid'));
    } else if (allKeysSet.has('user_id')) {
      baseColumns.push(createColumn('user_id'));
    }

    // 2a. Password plaintext column directly to the right of userid (if exists)
    if (allKeysSet.has('password_plaintext')) {
      baseColumns.push(createColumn('password_plaintext'));
    } else if (allKeysSet.has('passwordPlaintext')) {
      baseColumns.push(createColumn('passwordPlaintext'));
    }

    // 3. Location column (if exists)
    if (allKeysSet.has('location')) {
      baseColumns.push(createColumn('location'));
    }

    // 4. Multi Location User column (if exists)
    if (allKeysSet.has('multi_location_enable')) {
      baseColumns.push({
        accessorKey: 'multi_location_enable',
        header: 'Multi Location User',
        minSize: 180,
        size: 220,
        enableGlobalFilter: true,
        cell: info => {
          const value = info.getValue();
          return value ? 'Yes' : 'No';
        }
      });
    }

    // 5. Allocated roles / Roles / Role column (if exists)
    if (allKeysSet.has('allocated_roles')) {
      baseColumns.push(createColumn('allocated_roles'));
    } else if (allKeysSet.has('roles')) {
      baseColumns.push(createColumn('roles'));
    } else if (allKeysSet.has('role')) {
      baseColumns.push(createColumn('role'));
    }

    // 6. Created At column (if exists)
    if (allKeysSet.has('created_at')) {
      baseColumns.push({
        accessorKey: 'created_at',
        header: 'Created At',
        minSize: 180,
        size: 220,
        enableGlobalFilter: true,
        cell: info => {
          const value = info.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return value;
            }
          }
          return value ?? '-';
        }
      });
    }

    // 7a. Edit action column
    baseColumns.push({
      accessorKey: WMS_COL_EDIT,
      header: 'Edit',
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: false,
      minSize: 96,
      size: 108,
      cell: info => {
        const user = info.row.original;
        const uid = user?.userid ?? user?.user_id;
        const isDeleting = deleteLoading && deletingUserid === uid;

        return (
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(user);
            }}
            disabled={isDeleting}
            startIcon={<EditIcon fontSize="small" />}
            sx={{
              width: '100%',
              maxWidth: 140,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              justifyContent: 'flex-start',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Edit
          </Button>
        );
      }
    });

    // 7b. Delete action column
    baseColumns.push({
      accessorKey: WMS_COL_DELETE,
      header: 'Delete',
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: false,
      minSize: 96,
      size: 108,
      cell: info => {
        const user = info.row.original;
        const uid = user?.userid ?? user?.user_id;
        const isDeleting = deleteLoading && deletingUserid === uid;
        return (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(user);
            }}
            disabled={isDeleting || deleteLoading}
            sx={{
              width: '100%',
              maxWidth: 140,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              justifyContent: 'center',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            {isDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        );
      }
    });

    // Add any remaining columns that weren't in the fixed order
    const fixedOrderKeys = ['name', 'userid', 'user_id', 'password_plaintext', 'passwordPlaintext', 'location', 'multi_location_enable', 'allocated_roles', 'roles', 'role', 'created_at', WMS_COL_EDIT, WMS_COL_DELETE];
    const excludedKeys = ['features', 'feature_permissions'];
    allKeysSet.forEach(key => {
      if (!fixedOrderKeys.includes(key) && !excludedKeys.includes(key)) {
        const columnExists = baseColumns.some(col => col.accessorKey === key);
        if (!columnExists) {
          baseColumns.push(createColumn(key));
        }
      }
    });

    return baseColumns;
  }, [userList, deleteLoading, deletingUserid, visiblePasswords]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setCreateRolesMenuOpen(false);
    setForm(INITIAL_CREATE_FORM_STATE);
    setFormError('');
    setSuccess('');
    setShowPassword(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setCreateRolesMenuOpen(false);
    setForm(INITIAL_CREATE_FORM_STATE);
    setFormError('');
    setSuccess('');
    setShowPassword(false);
  }, []);

  const handleFormChange = useCallback((e) => {
    const { name, value, checked } = e.target;
    if (name === 'userid') {
      // Only allow alphanumeric, underscore, and dollar sign in userid
      const sanitizedValue = value.replace(/[^a-zA-Z0-9_$]/g, '');
      setForm(prev => ({ ...prev, [name]: sanitizedValue }));
    } else if (name === 'password') {
      // Remove spaces from password
      const trimmedValue = value.replace(/\s+/g, '');
      setForm(prev => ({ ...prev, [name]: trimmedValue }));
    } else if (name === 'name') {
      // Allow letters, numbers, spaces, and common punctuation for names
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s\-'\.]/g, '');
      setForm(prev => ({ ...prev, [name]: sanitizedValue }));
    } else if (name.startsWith('feature_')) {
      // Handle feature checkboxes
      const featureName = name.replace('feature_', '');
      setForm(prev => ({
        ...prev,
        features: {
          ...(prev.features || {}),
          [featureName]: checked
        }
      }));
    } else {
      // For other fields like location, use the value as is
      setForm(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    setSuccess('');

    // Validate required fields
    if (!form.name || !form.password || !form.location) {
      setFormError('All fields are required');
      setLoading(false);
      return;
    }

    // Validate password length
    if (form.password.length < 4) {
      setFormError('Password must be at least 4 characters long');
      setLoading(false);
      return;
    }

    // Validate maximum password length
    if (form.password.length > 15) {
      setFormError('Password cannot exceed 15 characters');
      setLoading(false);
      return;
    }

    // Validate no spaces in password
    if (form.password.includes(' ')) {
      setFormError('Password cannot contain spaces');
      setLoading(false);
      return;
    }

    // Validate name length
    if (form.name.trim().length < 2) {
      setFormError('Name must be at least 2 characters long');
      setLoading(false);
      return;
    }

    if (form.name.trim().length > 50) {
      setFormError('Name cannot exceed 50 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/admin/register-user', {
        name: form.name,
        password: form.password,
        location: form.location,
        multi_location_enable: form.multi_location_enable,
        hide_on_mobile: form.hide_on_mobile,
        features: form.features || {
          itemLookup: false,
          binCount: false,
          binAdjustments: false,
          binTransfer: false,
          inbound: false,
          ltlDashboard: false,
          ltlOrders: false,
          amzlPicking: false,
          amxlPicking: false,
          reassignLtlOrders: false,
          reassignAmzlOrders: false,
          dropshipReassign: false,
          rma: false,
          nonAmazonRegularShip: false,
          partsPicking: false,
          dropshipStatus: false,
          binAdjustmentsReport: false,
          binCountReports: false,
          binTransferReports: false,
          managerOtp: false,
          inboundReports: false,
          pickingReports: false,
          ltlReports: false,
          ftzBinTransfer: false,
          ftzBinPutaway: false,
          ftzShipping: false,
          ftzShippingOrder: false,
          ftzReceivingOrder: false,
          inventoryManagerApproval: false
        }
      });

      const newUserId = response?.data?.userid || response?.data?.user_id || response?.data?.id || form.userid;
      if (form.roles?.length && newUserId) {
        await assignRolesToUser(newUserId, form.roles);
        await refetchRoles();
      }

      setSuccess(form.roles?.length ? 'User created and roles assigned successfully!' : 'User created successfully!');
      setForm(INITIAL_CREATE_FORM_STATE);

      setTimeout(() => {
        setOpen(false);
        setSuccess('');
        refetch();
      }, 1000);
    } catch (error) {
      // Check if the error message indicates user already exists
      if (error.response?.data?.message?.toLowerCase().includes('already exists') ||
        error.response?.data?.detail?.toLowerCase().includes('already exists')) {
        setFormError('User already exists');
      } else {
        setFormError(error.response?.data?.message || error.response?.data?.detail || 'Failed to create user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const appGrid = useDataGridProTable({
    data: tableData,
    loading: isLoading,
    error: error ? error.message || 'Failed to load WMS AI users' : null,
    title: 'WMS Mobile Users',
    columns,
    getRowId: (row) => row?.id ?? row?.userid ?? row?.user_id,
    onRowClick: (row) => handleEditClick(row),
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
    actionButtons: [
      <Stack key="wms-actions" direction="row" spacing={1}>
        <Tooltip title="Add new user">
          <Button
            variant="contained"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            sx={{
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
          >
            Add User
          </Button>
        </Tooltip>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>,
    ]
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={appGrid} DataGridPro={DataGridPro} />
      </Box>

      {/* Create Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create WMS User
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Name"
                name="name"
                autoComplete="name"
                autoFocus
                value={form.name}
                onChange={handleFormChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={form.password}
                onChange={handleFormChange}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="location-label">Location</InputLabel>
                <Select
                  labelId="location-label"
                  id="location"
                  name="location"
                  value={form.location}
                  label="Location"
                  onChange={handleFormChange}
                  disabled={loading}
                >
                  {locationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel id="create-role-label">Roles</InputLabel>
                <Select
                  labelId="create-role-label"
                  id="roles"
                  name="roles"
                  multiple
                  open={createRolesMenuOpen}
                  onOpen={() => setCreateRolesMenuOpen(true)}
                  onClose={handleRolesMenuClose(setCreateRolesMenuOpen)}
                  value={Array.isArray(form.roles) ? form.roles : []}
                  label="Roles"
                  onChange={handleCreateRolesChange}
                  disabled={loading || rolesLoading}
                  MenuProps={roleSelectMenuProps(() => setCreateRolesMenuOpen(false), 'create')}
                  renderValue={(selected) => renderRoleSelectValue(selected)}
                >
                  {renderRoleMenuItems(form.roles)}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={form.multi_location_enable}
                    onChange={(e) => setForm(prev => ({ ...prev, multi_location_enable: e.target.checked }))}
                    name="multi_location_enable"
                    color="primary"
                    disabled={loading}
                  />
                  <Typography>Enable Multi-Location Access</Typography>
                </Stack>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={form.hide_on_mobile}
                    onChange={(e) => setForm(prev => ({ ...prev, hide_on_mobile: e.target.checked }))}
                    name="hide_on_mobile"
                    color="primary"
                    disabled={loading}
                  />
                  <Typography>Hide on Mobile</Typography>
                </Stack>
              </FormControl>

              {/* Features Section - Disabled (Users now get features via Role assignment) */}
              {false && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Features
                  </Typography>
                  <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 3,
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Select All Checkbox */}
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '2px solid #1976d2' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Object.values(form.features || {}).every(feature => feature === true)}
                            indeterminate={Object.values(form.features || {}).some(feature => feature === true) && !Object.values(form.features || {}).every(feature => feature === true)}
                            onChange={(e) => {
                              const allChecked = e.target.checked;
                              setForm(prev => ({
                                ...prev,
                                features: {
                                  itemLookup: allChecked,
                                  binCount: allChecked,
                                  binAdjustments: allChecked,
                                  binTransfer: allChecked,
                                  ltlDashboard: allChecked,
                                  inbound: allChecked,
                                  ftzInbound: allChecked,
                                  inboundReturnStatus: allChecked,
                                  dropShip: allChecked,
                                  amxlPicking: allChecked,
                                  shipConfirm: allChecked,
                                  dropshipStatus: allChecked,
                                  ltlOrders: allChecked,
                                  reassignLtlOrders: allChecked,
                                  dropshipReassign: allChecked,
                                  ftzBinTransfer: allChecked,
                                  ftzShippingOrder: allChecked,
                                  ftzReceivingOrder: allChecked,
                                  ftzQrGenerate: allChecked,
                                  binAdjustmentsReport: allChecked,
                                  binCountReports: allChecked,
                                  binTransferReports: allChecked,
                                  managerOtp: allChecked,
                                  inboundReports: allChecked,
                                  pickingReports: allChecked,
                                  ltlReports: allChecked
                                }
                              }));
                            }}
                            name="selectAll"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Select All Features"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </Box>
                    {/* Core Operations */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                      Core Operations
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.itemLookup || false}
                            onChange={handleFormChange}
                            name="feature_itemLookup"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Item Lookup"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binCount || false}
                            onChange={handleFormChange}
                            name="feature_binCount"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Count"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binAdjustments || false}
                            onChange={handleFormChange}
                            name="feature_binAdjustments"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Adjustments"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binTransfer || false}
                            onChange={handleFormChange}
                            name="feature_binTransfer"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Transfer"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ftzBinTransfer || false}
                            onChange={handleFormChange}
                            name="feature_ftzBinTransfer"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="FTZ - Bin Transfer"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.inbound || false}
                            onChange={handleFormChange}
                            name="feature_inbound"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Inbound Shipment"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ftzInbound || false}
                            onChange={handleFormChange}
                            name="feature_ftzInbound"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="FTZ - Inbound"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.inboundReturnStatus || false}
                            onChange={handleFormChange}
                            name="feature_inboundReturnStatus"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Inbound Return Status"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.dropShip || false}
                            onChange={handleFormChange}
                            name="feature_dropShip"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Regular Order Picking"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.shipConfirm || false}
                            onChange={handleFormChange}
                            name="feature_shipConfirm"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Regular Order Ship Confirm"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.dropshipStatus || false}
                            onChange={handleFormChange}
                            name="feature_dropshipStatus"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Regular Ship Status"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ltlDashboard || false}
                            onChange={handleFormChange}
                            name="feature_ltlDashboard"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="LTL Dashboard"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ltlOrders || false}
                            onChange={handleFormChange}
                            name="feature_ltlOrders"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="LTL Order Picking"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.reassignLtlOrders || false}
                            onChange={handleFormChange}
                            name="feature_reassignLtlOrders"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Re-Assign LTL Orders"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.dropshipReassign || false}
                            onChange={handleFormChange}
                            name="feature_dropshipReassign"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Re-Assign Regular Orders"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.rma || false}
                            onChange={handleFormChange}
                            name="feature_rma"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="RMA"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.nonAmazonRegularShip || false}
                            onChange={handleFormChange}
                            name="feature_nonAmazonRegularShip"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Non Amazon Regular Ship"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.partsPicking || false}
                            onChange={handleFormChange}
                            name="feature_partsPicking"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Parts picking"
                        sx={{ display: 'block', mb: 1 }}
                      />
                    </Box>

                    {/* Reports */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                      Reports
                    </Typography>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binAdjustmentsReport || false}
                            onChange={handleFormChange}
                            name="feature_binAdjustmentsReport"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Adjustments Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binCountReports || false}
                            onChange={handleFormChange}
                            name="feature_binCountReports"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Count Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.binTransferReports || false}
                            onChange={handleFormChange}
                            name="feature_binTransferReports"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Bin Transfer Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.managerOtp || false}
                            onChange={handleFormChange}
                            name="feature_managerOtp"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Manager OTP"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.inboundReports || false}
                            onChange={handleFormChange}
                            name="feature_inboundReports"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Inbound Shipment Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.pickingReports || false}
                            onChange={handleFormChange}
                            name="feature_pickingReports"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Pick Orders Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ltlReports || false}
                            onChange={handleFormChange}
                            name="feature_ltlReports"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="LTL Orders Records"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.regularReprint || false}
                            onChange={handleFormChange}
                            name="feature_regularReprint"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Regular Reprint"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.returnItem || false}
                            onChange={handleFormChange}
                            name="feature_returnItem"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Return Item"
                        sx={{ display: 'block', mb: 1 }}
                      />
                    </Box>

                    {/* FTZ */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                      FTZ
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ftzQrGenerate || false}
                            onChange={handleFormChange}
                            name="feature_ftzQrGenerate"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="FTZ QR Generate"
                        sx={{ display: 'block', mb: 1 }}
                      />
                    </Box>

                    {/* Transfer Order */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                      Transfer Order
                    </Typography>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ftzShippingOrder || false}
                            onChange={handleFormChange}
                            name="feature_ftzShippingOrder"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Shipping Order"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.features?.ftzReceivingOrder || false}
                            onChange={handleFormChange}
                            name="feature_ftzReceivingOrder"
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label="Receiving Order"
                        sx={{ display: 'block', mb: 1 }}
                      />
                    </Box>
                  </Box>
                </>
              )}
              {formError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {formError}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '400px' },
            m: { xs: 2, sm: 3 }
          }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete user {userToDelete?.userid}?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Password Dialog */}
      <Dialog
        open={updatePasswordDialogOpen}
        onClose={handleUpdatePasswordCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '400px' },
            m: { xs: 2, sm: 3 }
          }
        }}
      >
        <DialogTitle>
          Update Password
          <IconButton
            aria-label="Close update password dialog"
            onClick={handleUpdatePasswordCancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleUpdatePasswordSubmit}>
          <DialogContent dividers>
            {updatePasswordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updatePasswordError}
              </Alert>
            )}
            {updatePasswordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {updatePasswordSuccess}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="update-userid"
              label="User ID"
              name="userid"
              value={updatePasswordForm.userid}
              disabled={true}
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="new-password"
              label="New Password"
              name="new_password"
              type={showUpdatePassword ? 'text' : 'password'}
              value={updatePasswordForm.new_password}
              onChange={handleUpdatePasswordChange}
              error={!!updatePasswordError && updatePasswordError.toLowerCase().includes('password')}
              disabled={updatePasswordLoading}
              inputProps={{ maxLength: 15 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowUpdatePassword(!showUpdatePassword)}
                      edge="end"
                      aria-label={showUpdatePassword ? "Hide password" : "Show password"}
                    >
                      {showUpdatePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleUpdatePasswordCancel} disabled={updatePasswordLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={updatePasswordLoading}
            >
              {updatePasswordLoading ? <CircularProgress size={20} /> : 'Update Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          Edit WMS User
          <IconButton
            aria-label="close"
            onClick={handleEditClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleEditSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="User ID"
              name="userid"
              value={editForm.userid}
              disabled
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="name"
              label="Name"
              value={editForm.name}
              onChange={handleEditFormChange}
              disabled={editLoading}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="edit-location-label">Location</InputLabel>
              <Select
                labelId="edit-location-label"
                id="edit-location"
                name="location"
                value={editForm.location}
                label="Location"
                onChange={handleEditFormChange}
                disabled={editLoading}
              >
                {locationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel id="edit-role-label">Roles</InputLabel>
              <Select
                labelId="edit-role-label"
                id="edit-roles"
                name="roles"
                multiple
                open={editRolesMenuOpen}
                onOpen={() => setEditRolesMenuOpen(true)}
                onClose={handleRolesMenuClose(setEditRolesMenuOpen)}
                value={editRoleLoading ? [] : editForm.roles}
                label="Roles"
                onChange={handleEditRolesChange}
                disabled={editLoading || rolesLoading || editRoleLoading}
                MenuProps={roleSelectMenuProps(() => setEditRolesMenuOpen(false), 'edit')}
                renderValue={(selected) => renderRoleSelectValue(selected, editRoleLoading)}
              >
                {renderRoleMenuItems(editForm.roles)}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label="New Password (Optional)"
              type={showEditPassword ? 'text' : 'password'}
              value={editForm.password}
              onChange={handleEditFormChange}
              disabled={editLoading}
              helperText="Leave blank to keep current password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      edge="end"
                    >
                      {showEditPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              name="confirm_password"
              label="Confirm New Password"
              type={showEditConfirmPassword ? 'text' : 'password'}
              value={editForm.confirm_password}
              onChange={handleEditFormChange}
              disabled={editLoading}
              helperText="Enter new password above first"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                      edge="end"
                    >
                      {showEditConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormControl fullWidth margin="normal">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={editForm.multi_location_enable}
                  onChange={handleEditFormChange}
                  name="multi_location_enable"
                  disabled={editLoading}
                />
                <Typography>Enable Multi-Location Access</Typography>
              </Stack>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={editForm.hide_on_mobile}
                  onChange={handleEditFormChange}
                  name="hide_on_mobile"
                  disabled={editLoading}
                />
                <Typography>Hide on Mobile</Typography>
              </Stack>
            </FormControl>

            {/* Features Section - Disabled (Users now get features via Role assignment) */}
            {false && (
              <>
                <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  Features
                </Typography>
                <Box sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3,
                  backgroundColor: '#fafafa'
                }}>
                  {/* Core Operations */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Core Operations
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.itemLookup || false}
                          onChange={handleEditFormChange}
                          name="feature_itemLookup"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Item Lookup"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binCount || false}
                          onChange={handleEditFormChange}
                          name="feature_binCount"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Count"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binAdjustments || false}
                          onChange={handleEditFormChange}
                          name="feature_binAdjustments"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Adjustments"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binTransfer || false}
                          onChange={handleEditFormChange}
                          name="feature_binTransfer"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Transfer"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.inbound || false}
                          onChange={handleEditFormChange}
                          name="feature_inbound"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Inbound Shipment"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ftzInbound || false}
                          onChange={handleEditFormChange}
                          name="feature_ftzInbound"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="FTZ - Inbound"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.inboundReturnStatus || false}
                          onChange={handleEditFormChange}
                          name="feature_inboundReturnStatus"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Inbound Return Status"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.dropShip || false}
                          onChange={handleEditFormChange}
                          name="feature_dropShip"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Regular Order Picking"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.shipConfirm || false}
                          onChange={handleEditFormChange}
                          name="feature_shipConfirm"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Regular Order Ship Confirm"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.dropshipStatus || false}
                          onChange={handleEditFormChange}
                          name="feature_dropshipStatus"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Regular Ship Status"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ltlDashboard || false}
                          onChange={handleEditFormChange}
                          name="feature_ltlDashboard"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="LTL Dashboard"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ltlOrders || false}
                          onChange={handleEditFormChange}
                          name="feature_ltlOrders"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="LTL Order Picking"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.reassignLtlOrders || false}
                          onChange={handleEditFormChange}
                          name="feature_reassignLtlOrders"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Re-Assign LTL Orders"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.dropshipReassign || false}
                          onChange={handleEditFormChange}
                          name="feature_dropshipReassign"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Re-Assign Regular Orders"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.rma || false}
                          onChange={handleEditFormChange}
                          name="feature_rma"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="RMA"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.nonAmazonRegularShip || false}
                          onChange={handleEditFormChange}
                          name="feature_nonAmazonRegularShip"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Non Amazon Regular Ship"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.partsPicking || false}
                          onChange={handleEditFormChange}
                          name="feature_partsPicking"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Parts picking"
                      sx={{ display: 'block', mb: 1 }}
                    />
                  </Box>

                  {/* Reports */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Reports
                  </Typography>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binAdjustmentsReport || false}
                          onChange={handleEditFormChange}
                          name="feature_binAdjustmentsReport"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Adjustments Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binCountReports || false}
                          onChange={handleEditFormChange}
                          name="feature_binCountReports"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Count Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.binTransferReports || false}
                          onChange={handleEditFormChange}
                          name="feature_binTransferReports"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Bin Transfer Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.managerOtp || false}
                          onChange={handleEditFormChange}
                          name="feature_managerOtp"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Manager OTP"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.inboundReports || false}
                          onChange={handleEditFormChange}
                          name="feature_inboundReports"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Inbound Shipment Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.pickingReports || false}
                          onChange={handleEditFormChange}
                          name="feature_pickingReports"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Pick Orders Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ltlReports || false}
                          onChange={handleEditFormChange}
                          name="feature_ltlReports"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="LTL Orders Records"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.regularReprint || false}
                          onChange={handleEditFormChange}
                          name="feature_regularReprint"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Regular Reprint"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.returnItem || false}
                          onChange={handleEditFormChange}
                          name="feature_returnItem"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Return Item"
                      sx={{ display: 'block', mb: 1 }}
                    />
                  </Box>

                  {/* FTZ */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    FTZ
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ftzQrGenerate || false}
                          onChange={handleEditFormChange}
                          name="feature_ftzQrGenerate"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="FTZ QR Generate"
                      sx={{ display: 'block', mb: 1 }}
                    />
                  </Box>

                  {/* Transfer Order */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Transfer Order
                  </Typography>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ftzShippingOrder || false}
                          onChange={handleEditFormChange}
                          name="feature_ftzShippingOrder"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Shipping Order"
                      sx={{ display: 'block', mb: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.features?.ftzReceivingOrder || false}
                          onChange={handleEditFormChange}
                          name="feature_ftzReceivingOrder"
                          color="primary"
                          disabled={editLoading}
                        />
                      }
                      label="Receiving Order"
                      sx={{ display: 'block', mb: 1 }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {editError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {editError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
            endIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default WmsAiUsers; 